import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fromZonedTime } from "date-fns-tz";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { useSchedulingParticipants } from "src/hooks/useSchedulingParticipants";
import { useTimezone } from "src/hooks/useTimezone";
import { SchedulingParticipant } from "src/types/scheduling";
import { PersonCommandOption } from "src/components/ui/PersonCommandCombobox";
import { useToast } from "src/hooks/use-toast";
import {
  createAdminOnDemandSession,
  createAdminRecurringSessions,
  fetchBatchCurriculumUnitsForScheduling,
  fetchSchedulingPurchasedPlans,
} from "src/services/adminSchedulingService";
import { SessionSchedulingType } from "src/services/sessionsService";

import {
  SchedulingBreadcrumbBar,
  SessionModeAndPlanCard,
  SessionRosterManager,
  SessionScheduleTimingCard,
  SessionNotesCard,
  BatchEntitlementLedgerCard,
  MeetingProviderCard,
  SchedulingActionTray,
} from "src/components/admin/scheduling";

const WEEKDAYS = [
  { value: "0", label: "Sunday", short: "Sun" },
  { value: "1", label: "Monday", short: "Mon" },
  { value: "2", label: "Tuesday", short: "Tue" },
  { value: "3", label: "Wednesday", short: "Wed" },
  { value: "4", label: "Thursday", short: "Thu" },
  { value: "5", label: "Friday", short: "Fri" },
  { value: "6", label: "Saturday", short: "Sat" },
];

const mapParticipantToOption = (participant: SchedulingParticipant): PersonCommandOption => ({
  value: participant.id,
  label: [participant.firstName, participant.lastName].filter(Boolean).join(" ") || participant.email,
  description: participant.description,
  keywords: participant.keywords,
  recent: participant.recent,
});

const ScheduleSession = () => {
  const navigate = useNavigate();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  const { toast } = useToast();
  
  // Timezone and participants hooks
  const { detectedTimezone } = useTimezone();
  const {
    data: schedulingParticipants,
    isLoading: isParticipantsLoading,
    isError: hasParticipantsError,
    error: participantsError,
  } = useSchedulingParticipants();

  const {
    data: purchasedPlans = [],
    isLoading: isPlansLoading,
    isError: hasPlansError,
    error: plansError,
  } = useQuery({
    queryKey: ["admin", "scheduling-purchased-plans", "with-subject-fallback-v2"],
    queryFn: fetchSchedulingPurchasedPlans,
    staleTime: 5 * 60 * 1000,
  });

  const studentOptions = (schedulingParticipants?.students || []).map(mapParticipantToOption);
  const tutorOptions = (schedulingParticipants?.tutors || []).map(mapParticipantToOption);

  // Form State
  const [schedulingType, setSchedulingType] = useState<SessionSchedulingType>("on_demand");
  const [date, setDate] = useState("2026-11-02");
  const [startTime, setStartTime] = useState("16:30");
  const [duration, setDuration] = useState("60");
  const [timezone, setTimezone] = useState(detectedTimezone || "Asia/Kolkata");
  const [tutor, setTutor] = useState("");
  const [tutorTimezone, setTutorTimezone] = useState("Europe/London");
  const [student, setStudent] = useState("");
  const [additionalStudentIds, setAdditionalStudentIds] = useState<string[]>([]);
  const [studentTimezone, setStudentTimezone] = useState("America/New_York");
  const [notes, setNotes] = useState("");
  const [batchId, setBatchId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [weekdays, setWeekdays] = useState<number[]>([1, 3]); // Monday & Wednesday default
  const [startDate, setStartDate] = useState("2026-11-02");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedPlan = useMemo(
    () => purchasedPlans.find((plan) => plan.id === batchId),
    [batchId, purchasedPlans]
  );
  const selectedPlanCapacity = selectedPlan?.remaining_sessions ?? 0;
  const canLoadBatchUnits = Boolean(batchId);

  const {
    data: batchUnits = [],
    isLoading: isBatchUnitsLoading,
  } = useQuery({
    queryKey: ["admin", "scheduling-batch-curriculum-units", batchId],
    queryFn: () => fetchBatchCurriculumUnitsForScheduling(batchId),
    enabled: canLoadBatchUnits,
    staleTime: 5 * 60 * 1000,
  });

  const handleStudentChange = (value: string) => {
    setStudent(value);
    setAdditionalStudentIds((current) => current.filter((studentId) => studentId !== value));
    const selectedStudent = schedulingParticipants?.students.find((item) => item.id === value);
    if (selectedStudent?.timezone) {
      setStudentTimezone(selectedStudent.timezone);
    }
  };

  const handleTutorChange = (value: string) => {
    setTutor(value);
    const selectedTutor = schedulingParticipants?.tutors.find((item) => item.id === value);
    if (selectedTutor?.timezone) {
      setTutorTimezone(selectedTutor.timezone);
    }
  };

  const handleBatchChange = (value: string) => {
    const nextBatchId = value === "none" ? "" : value;
    setBatchId(nextBatchId);
    setUnitId("");
    setAdditionalStudentIds([]);

    const plan = purchasedPlans.find((item) => item.id === nextBatchId);
    if (!plan) return;

    handleStudentChange(plan.student_id);
    if (plan.default_tutor_id) {
      handleTutorChange(plan.default_tutor_id);
    }
  };

  const handleSchedulingTypeChange = (nextSchedulingType: SessionSchedulingType) => {
    setSchedulingType(nextSchedulingType);
    if (nextSchedulingType === "recurring") setUnitId("");
  };

  const handleAdditionalStudentChange = (value: string) => {
    if (!value || value === student) return;
    setAdditionalStudentIds((current) => (
      current.includes(value) ? current : [...current, value]
    ));
  };

  const handleRemoveAdditionalStudent = (studentId: string) => {
    setAdditionalStudentIds((current) => current.filter((id) => id !== studentId));
  };

  const selectedStudentIds = useMemo(
    () => Array.from(new Set([student, ...additionalStudentIds].filter(Boolean))),
    [student, additionalStudentIds]
  );
  const additionalStudentOptions = studentOptions.filter(
    (option) => !selectedStudentIds.includes(option.value)
  );
  const participantCount = (tutor ? 1 : 0) + selectedStudentIds.length;

  const getTutorLabel = (tutorId: string) =>
    tutorOptions.find((option) => option.value === tutorId)?.label || "Selected tutor";
  const getStudentLabel = (studentId: string) =>
    studentOptions.find((option) => option.value === studentId)?.label || "Selected student";

  const selectedWeekdayLabels = WEEKDAYS
    .filter((day) => weekdays.includes(Number(day.value)))
    .map((day) => day.label);

  const toggleWeekday = (weekday: number, checked: boolean) => {
    setWeekdays((current) => (
      checked
        ? Array.from(new Set([...current, weekday])).sort((a, b) => a - b)
        : current.filter((value) => value !== weekday)
    ));
  };

  // Projected recurrence dates calculation (FIFO allocation)
  const projectedRecurringDates = useMemo(() => {
    if (schedulingType !== "recurring" || !startDate || !weekdays.length) {
      return [];
    }
    const targetCount = selectedPlanCapacity > 0 ? selectedPlanCapacity : 8;
    const dates: { dateStr: string; weekdayShort: string; formatted: string }[] = [];
    const cursor = new Date(`${startDate}T00:00:00`);
    let count = 0;
    const maxIterations = 365;
    let iter = 0;
    while (count < targetCount && iter < maxIterations) {
      const dayOfWeek = cursor.getDay();
      if (weekdays.includes(dayOfWeek)) {
        dates.push({
          dateStr: cursor.toISOString().slice(0, 10),
          weekdayShort: WEEKDAYS.find((w) => Number(w.value) === dayOfWeek)?.short || "",
          formatted: cursor.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
        });
        count++;
      }
      cursor.setDate(cursor.getDate() + 1);
      iter++;
    }
    return dates;
  }, [schedulingType, startDate, weekdays, selectedPlanCapacity]);

  const calculatedSeriesEndDate = projectedRecurringDates.length > 0
    ? projectedRecurringDates[projectedRecurringDates.length - 1].formatted
    : "--";

  const scheduledDatesCount = schedulingType === "recurring"
    ? projectedRecurringDates.length
    : 1;

  const remainingAfterSave = Math.max(0, selectedPlanCapacity - scheduledDatesCount);

  // Set default timezone when detected
  useEffect(() => {
    if (detectedTimezone && !timezone) {
      setTimezone(detectedTimezone);
    }
  }, [detectedTimezone, timezone]);

  useEffect(() => {
    setTitle("Schedule Session");
    setDescription("Create a new tutoring session by assigning tutors and students");
    setActions(null);

    return () => reset();
  }, [setTitle, setDescription, setActions, reset]);

  const toScheduledAt = (sessionDate: string, sessionTime: string) => {
    return fromZonedTime(`${sessionDate}T${sessionTime}:00`, timezone).toISOString();
  };

  const getSessionDetails = () => {
    const description = [selectedPlan?.subjectName, selectedPlan?.product_name]
      .filter(Boolean)
      .join(" • ");

    return notes || description
      ? {
          notes: notes || undefined,
          description: description || undefined,
        }
      : undefined;
  };

  const validateForm = () => {
    if (!startTime) return "Choose a start time.";
    if (!tutor) return "Choose a tutor.";

    if (schedulingType === "on_demand") {
      if (!student) return "Choose a student.";
      if (!date) return "Choose a date.";
      return null;
    }

    if (!batchId) return "Choose a purchased plan or batch.";
    if (!weekdays.length) return "Choose at least one weekday.";
    if (!startDate) return "Choose a recurrence start date.";
    if (selectedPlanCapacity <= 0) return "The selected purchased plan has no remaining sessions.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateForm();

    if (validationError) {
      toast({
        title: "Missing scheduling details",
        description: validationError,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      if (schedulingType === "on_demand") {
        await createAdminOnDemandSession({
          batch_id: batchId || null,
          curriculum_unit_id: unitId || null,
          tutor_id: tutor,
          student_id: student,
          participants: [
            { user_id: tutor, participant_role: "tutor" },
            ...selectedStudentIds.map((studentId) => ({ user_id: studentId, participant_role: "student" as const })),
          ],
          scheduled_at: toScheduledAt(date, startTime),
          duration_min: Number(duration),
          created_by: "admin",
          session_details: getSessionDetails(),
        });

        toast({
          title: "Session scheduled",
          description: "The on-demand session has been created.",
        });
      } else {
        const sessions = await createAdminRecurringSessions({
          batch_id: batchId,
          curriculum_unit_id: unitId || null,
          tutor_id: tutor,
          student_id: selectedPlan?.student_id || student,
          participants: [
            { user_id: tutor, participant_role: "tutor" },
            ...selectedStudentIds.map((studentId) => ({ user_id: studentId, participant_role: "student" as const })),
          ],
          scheduled_at: toScheduledAt(startDate, startTime),
          duration_min: Number(duration),
          created_by: "admin",
          weekdays,
          start_date: startDate,
          timezone,
          session_details: getSessionDetails(),
        });

        toast({
          title: "Recurring sessions scheduled",
          description: `${sessions.length} session${sessions.length === 1 ? "" : "s"} created from the purchased plan.`,
        });
      }

      navigate("/admin/sessions");
    } catch (error) {
      toast({
        title: "Unable to schedule session",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectSlot = (slotDate: string, slotLocalTime: string) => {
    setDate(slotDate);
    setStartTime(slotLocalTime);
    toast({
      title: "Slot selected",
      description: `Target set to ${slotDate} at ${slotLocalTime} (Collision-Free).`,
    });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-12 font-sans antialiased">
      {/* Top Operational Status Bar */}
      <SchedulingBreadcrumbBar />

      <form id="schedule-session-form" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* Left Column: Form Workbench (8 cols) */}
          <div className="lg:col-span-8 space-y-6">
            {/* Step 1: Cadence & Plan */}
            <SessionModeAndPlanCard
              schedulingType={schedulingType}
              onSchedulingTypeChange={handleSchedulingTypeChange}
              batchId={batchId}
              onBatchChange={handleBatchChange}
              purchasedPlans={purchasedPlans}
              isPlansLoading={isPlansLoading}
              hasPlansError={hasPlansError}
              plansError={plansError}
              selectedPlan={selectedPlan}
              unitId={unitId}
              onUnitChange={setUnitId}
              batchUnits={batchUnits}
              isBatchUnitsLoading={isBatchUnitsLoading}
            />

            {/* Step 2: Participants & Roster */}
            <SessionRosterManager
              tutor={tutor}
              onTutorChange={handleTutorChange}
              tutorOptions={tutorOptions}
              student={student}
              onStudentChange={handleStudentChange}
              studentOptions={studentOptions}
              additionalStudentIds={additionalStudentIds}
              onAdditionalStudentChange={handleAdditionalStudentChange}
              onRemoveAdditionalStudent={handleRemoveAdditionalStudent}
              additionalStudentOptions={additionalStudentOptions}
              isParticipantsLoading={isParticipantsLoading}
              hasParticipantsError={hasParticipantsError}
              participantsError={participantsError}
              participantCount={participantCount}
              schedulingType={schedulingType}
            />

            {/* Step 3: Schedule, Timing & Availability Radar */}
            <SessionScheduleTimingCard
              schedulingType={schedulingType}
              tutor={tutor}
              tutorName={getTutorLabel(tutor)}
              tutorTimezone={tutorTimezone}
              studentName={getStudentLabel(student)}
              studentTimezone={studentTimezone}
              date={date}
              onDateChange={setDate}
              startDate={startDate}
              onStartDateChange={setStartDate}
              startTime={startTime}
              onStartTimeChange={setStartTime}
              duration={duration}
              onDurationChange={setDuration}
              timezone={timezone}
              onTimezoneChange={setTimezone}
              weekdays={weekdays}
              onToggleWeekday={toggleWeekday}
              weekdayOptions={WEEKDAYS}
              selectedWeekdayLabels={selectedWeekdayLabels}
              projectedRecurringDates={projectedRecurringDates}
              calculatedSeriesEndDate={calculatedSeriesEndDate}
              onSelectSlot={handleSelectSlot}
            />

            {/* Step 4: Notes & Topic Objectives */}
            <SessionNotesCard notes={notes} onNotesChange={setNotes} />
          </div>

          {/* Right Column: Persistent Live Inspector (4 cols) */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-6">
            <BatchEntitlementLedgerCard
              selectedPlan={selectedPlan}
              selectedPlanCapacity={selectedPlanCapacity}
              scheduledDatesCount={scheduledDatesCount}
              remainingAfterSave={remainingAfterSave}
              schedulingType={schedulingType}
            />

            <MeetingProviderCard
              schedulingType={schedulingType}
              scheduledDatesCount={scheduledDatesCount}
            />

            <SchedulingActionTray
              isSubmitting={isSubmitting}
              schedulingType={schedulingType}
              scheduledDatesCount={scheduledDatesCount}
            />
          </div>

        </div>
      </form>
    </div>
  );
};

export default ScheduleSession;
